import { forwardRef, useImperativeHandle } from 'react';
import { PlaybookTransformItem, PlaybookEventConfig } from '@/types/playbook';
import { UnifiedSchema, EventSchema } from '@/types/schema';
import PlaybookAIMappingProgressDialog from '@/components/dialogs/PlaybookAIMappingProgressDialog';
import MappingSummaryDialog from '@/components/dialogs/MappingSummaryDialog';
import { usePlaybookAIMapping } from '@/hooks/usePlaybookAIMapping';
import { usePlaybookAutoMapping } from '@/hooks/usePlaybookAutoMapping';
import { EditorSelection } from '../PlaybookEditorTab';

interface PlaybookAIMappingSectionProps {
  inputSchema: UnifiedSchema | null;
  outputSchema: UnifiedSchema | null;
  transforms: PlaybookTransformItem[];
  onAddTransforms: (transforms: PlaybookTransformItem[]) => void;
  onUpdateTransform: (transformId: string, updates: Partial<PlaybookTransformItem>) => void;
  onBatchUpdateTransforms?: (batchUpdates: { [transformId: string]: Partial<PlaybookTransformItem> }) => void;
  selection: EditorSelection;
  allEvents: EventSchema[];
  uiEventConfigs: PlaybookEventConfig[];
  allUiTransforms: PlaybookTransformItem[];
  allowUndefinedTransforms: boolean;
  baselineSchema?: 'input' | 'output';
  isPropertyPairApplicableToAllEvents: (inputPath: string | undefined, outputPath: string | undefined) => boolean;
}

export interface PlaybookAIMappingSectionRef {
  handleAutoMap: () => void;
  handleAIMatch: () => void;
}

const PlaybookAIMappingSection = forwardRef<PlaybookAIMappingSectionRef, PlaybookAIMappingSectionProps>(({
  inputSchema,
  outputSchema,
  transforms,
  onAddTransforms,
  onUpdateTransform,
  onBatchUpdateTransforms,
  selection,
  uiEventConfigs,
  allUiTransforms,
  allowUndefinedTransforms,
  baselineSchema,
  isPropertyPairApplicableToAllEvents,
}, ref) => {
  // Use the AI mapping hook
  const {
    showAIMappingProgressDialog,
    aiProgress,
    aiProgressDetail,
    aiMappingError,
    aiMappingComplete,
    aiMappingStats,
    aiMappingPartialSuccess,
    showAIMappingSummary,
    aiMappingSummaryData,
    handleAIConflictResolution,
    setShowAIMappingSummary,
    handleAIMatch,
    handleRetryAIMapping,
    setShowAIMappingProgressDialog,
    aiMappingAbortController
  } = usePlaybookAIMapping({
    inputSchema,
    outputSchema,
    transforms,
    onAddTransforms,
    onUpdateTransform,
    onBatchUpdateTransforms,
    selection,
    allUiTransforms,
    allowUndefinedTransforms,
    baselineSchema,
    isPropertyPairApplicableToAllEvents,
  });

  // Use the auto mapping hook
  const {
    handleAutoMap,
    showMappingSummary,
    mappingSummaryData,
    handleConflictResolution,
    setShowMappingSummary
  } = usePlaybookAutoMapping({
    inputSchema,
    outputSchema,
    transforms,
    onAddTransforms,
    onUpdateTransform,
    selection,
    allUiTransforms,
    allowUndefinedTransforms,
    baselineSchema,
    isPropertyPairApplicableToAllEvents,
    allEvents: uiEventConfigs.map(config => ({ id: config.id })),
    uiEventConfigs,
  });

  // Expose the functions to parent components via ref
  useImperativeHandle(ref, () => ({
    handleAutoMap,
    handleAIMatch,
  }), [handleAutoMap, handleAIMatch]);

  return (
    <>
      <PlaybookAIMappingProgressDialog
        isOpen={showAIMappingProgressDialog}
        progress={aiProgress}
        progressDetail={aiProgressDetail}
        error={aiMappingError}
        isComplete={aiMappingComplete}
        stats={aiMappingStats}
        hasPartialSuccess={aiMappingPartialSuccess}
        onClose={() => {
          setShowAIMappingProgressDialog(false);
        }}
        onCancel={() => {
          if (aiMappingAbortController) {
            aiMappingAbortController.abort();
          }
          setShowAIMappingProgressDialog(false);
        }}
        onRetry={handleRetryAIMapping}
      />
      
      {mappingSummaryData && (
        <MappingSummaryDialog
          isOpen={showMappingSummary}
          summaryData={mappingSummaryData}
          onConfirm={handleConflictResolution}
          onCancel={() => setShowMappingSummary(false)}
        />
      )}
      
      {aiMappingSummaryData && (
        <MappingSummaryDialog
          isOpen={showAIMappingSummary}
          summaryData={aiMappingSummaryData}
          onConfirm={handleAIConflictResolution}
          onCancel={() => setShowAIMappingSummary(false)}
        />
      )}
    </>
  );
});

PlaybookAIMappingSection.displayName = 'PlaybookAIMappingSection';

export default PlaybookAIMappingSection;
